import { noop } from 'lodash';
import type { FC, PropsWithChildren } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import type { Announce } from './Announcer';
import Announcer from './Announcer';
import AnnouncerContext from './AnnouncerContext';

const AnnouncerProvider: FC<PropsWithChildren> = ({ children }) => {
  const announceImplRef = useRef<Announce>(noop);
  const activeChildAnnouncerRef = useRef<Announce | null>(null);
  const parentAnnouncerProvider = useContext(AnnouncerContext);

  const setAnnounce = (value: Announce) => {
    announceImplRef.current = value;
  };

  const announce = useCallback(
    (text: string) => {
      const announceImpl = activeChildAnnouncerRef.current ?? announceImplRef.current;

      announceImpl(text);
    },
    [activeChildAnnouncerRef]
  );

  useEffect(() => {
    parentAnnouncerProvider.setActiveChildAnnouncer(announce);

    return () => {
      parentAnnouncerProvider.clearActiveChildAnnouncer();
    };
  }, [announce, parentAnnouncerProvider]);

  const setActiveChildAnnouncer = useCallback((value: Announce) => {
    activeChildAnnouncerRef.current = value;
  }, []);

  const clearActiveChildAnnouncer = useCallback(() => {
    activeChildAnnouncerRef.current = null;
  }, []);

  const contextValue = useMemo(
    () => ({
      announce,
      setActiveChildAnnouncer,
      clearActiveChildAnnouncer,
    }),
    [announce, setActiveChildAnnouncer, clearActiveChildAnnouncer]
  );

  return (
    <>
      <AnnouncerContext.Provider value={contextValue}>{children}</AnnouncerContext.Provider>
      <Announcer announceRef={setAnnounce} />
    </>
  );
};

export default AnnouncerProvider;
